import { createViewDay, createViewMonthGrid, createViewWeek } from '@schedule-x/calendar';
import { createCalendarControlsPlugin } from '@schedule-x/calendar-controls';
import { createCurrentTimePlugin } from '@schedule-x/current-time';
import { ScheduleXCalendar, useCalendarApp } from '@schedule-x/react';
import { createScrollControllerPlugin } from '@schedule-x/scroll-controller';
import '@schedule-x/theme-default/dist/index.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Temporal } from 'temporal-polyfill';

import { useTimeZone } from '@/auth/useTimeZone';

import { SessionNextClassTooltip } from './session-next-class-tooltip';
import './schedule-x-calendar.css';
import { SessionScheduleEventPopover } from './session-schedule-calendar-event-popover';
import {
  SCHEDULE_X_CUSTOM_COMPONENTS,
  ScheduleXEventsByIdContext,
} from './session-schedule-calendar-schedule-x.components';
import { ScheduleXToolbar } from './session-schedule-calendar-toolbar-schedule-x';
import {
  SCHEDULE_X_GRID_STEP_MINUTES,
  SCHEDULE_X_MONTH_GRID_N_EVENTS_PER_DAY,
  SCHEDULE_X_MONTH_GRID_VISIBLE_EVENTS,
  SCHEDULE_X_WEEK_GRID_HEIGHT_PX,
  coachCalendarEventCountByDate,
  flushScheduleXMonthGridSync,
  formatScheduleXRangeLabel,
  formatToolbarLabelFromActiveInstant,
  syncScheduleXMonthGridDayCells,
  toScheduleXViewName,
} from './session-schedule-calendar-schedule-x.utils';
import { EventContext, substitutionRequestSummariesForSessions } from './session-schedule-calendar-shared';
import type { CalendarView, SessionScheduleProps } from './session-schedule-calendar-types';
import {
  concreteEventToScheduleX,
  fromScheduleXPlainDate,
  instantIntervalFromScheduleXRange,
  noonInstantOnPlainDate,
  scheduleXRangeToPolyfill,
  scheduleXTimeString,
  toScheduleXApiPlainDate,
} from './schedule-x-temporal';

function buildScheduleXViews(views: CalendarView[]) {
  const viewFactories = {
    month: createViewMonthGrid,
    week: createViewWeek,
    day: createViewDay,
  } as const;
  const built = views.map((v) => viewFactories[v]());
  type View = (typeof built)[number];
  return built as [View, ...View[]];
}

//#region Main calendar (Schedule-X implementation behind feature flag)

export function SessionScheduleCalendarScheduleX({
  isLoading = false,
  events,
  sessionById,
  activeInstant,
  setActiveInstant,
  onRangeChange,
  views,
  view,
  onView,
  navigateToDayAt,
  toolbarLeftAddon,
}: SessionScheduleProps) {
  const { timeZone } = useTimeZone();
  const [toolbarLabel, setToolbarLabel] = useState('');
  const fallbackToolbarLabel = useMemo(
    () => formatToolbarLabelFromActiveInstant(activeInstant, timeZone, view),
    [activeInstant, timeZone, view],
  );
  const displayToolbarLabel = toolbarLabel || fallbackToolbarLabel;
  const [activeEventId, setActiveEventId] = useState<string | null>(null);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLElement | null>(null);
  const clearActiveEvent = useCallback(() => {
    setActiveEventId(null);
    setPopoverAnchorEl(null);
  }, []);

  const eventsById = useMemo(() => new Map(events.map((e) => [e.id, e])), [events]);

  const substitutionRequestSummaryBySessionId = useMemo(
    () => substitutionRequestSummariesForSessions(sessionById),
    [sessionById],
  );

  const eventContext = useMemo(
    () => ({
      isLoading,
      activeEventId,
      sessionById,
      clearActiveEvent,
      substitutionRequestSummaryBySessionId,
      view,
      toolbarLeftAddon: toolbarLeftAddon ?? null,
    }),
    [
      isLoading,
      activeEventId,
      sessionById,
      clearActiveEvent,
      substitutionRequestSummaryBySessionId,
      view,
      toolbarLeftAddon,
    ],
  );

  const calendarControls = useMemo(() => createCalendarControlsPlugin(), []);
  const scrollController = useMemo(
    () => createScrollControllerPlugin({ initialScroll: scheduleXTimeString(activeInstant, timeZone) }),
    // initialScroll applies on first grid render; later scrolls use scrollTo after render only
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const currentTime = useMemo(() => createCurrentTimePlugin(), []);

  const setActiveInstantRef = useRef(setActiveInstant);
  const onViewRef = useRef(onView);
  const onRangeChangeRef = useRef(onRangeChange);
  const viewRef = useRef(view);
  const viewsRef = useRef(views);
  const timeZoneRef = useRef(timeZone);
  const calendarControlsRef = useRef(calendarControls);
  const navigateToDayAtRef = useRef(navigateToDayAt);
  const pendingDayDrillDownRef = useRef(false);
  const scrollTimeRef = useRef(scheduleXTimeString(activeInstant, timeZone));
  const scrollControllerRef = useRef(scrollController);
  const calendarRenderedRef = useRef(false);
  const appliedTimezoneRef = useRef(timeZone);

  setActiveInstantRef.current = setActiveInstant;
  onViewRef.current = onView;
  onRangeChangeRef.current = onRangeChange;
  viewRef.current = view;
  viewsRef.current = views;
  timeZoneRef.current = timeZone;
  calendarControlsRef.current = calendarControls;
  navigateToDayAtRef.current = navigateToDayAt;
  scrollTimeRef.current = scheduleXTimeString(activeInstant, timeZone);
  scrollControllerRef.current = scrollController;

  const syncUrlFromPlainDate = useCallback((plainDate: Temporal.PlainDate) => {
    setActiveInstantRef.current(noonInstantOnPlainDate(plainDate, timeZoneRef.current));
  }, []);

  const scrollTimeGridToRef = useCallback(() => {
    if (viewRef.current === 'month') {
      return;
    }
    queueMicrotask(() => {
      try {
        scrollControllerRef.current.scrollTo(scrollTimeRef.current);
      } catch {
        // scroll-controller is not ready until the time grid exists
      }
    });
  }, []);

  const drillDownToDayView = useCallback((date: Temporal.PlainDate) => {
    if (!viewsRef.current.includes('day')) {
      return;
    }
    pendingDayDrillDownRef.current = true;
    calendarControlsRef.current.setView('day');
    calendarControlsRef.current.setDate(toScheduleXApiPlainDate(date));
    const instant = noonInstantOnPlainDate(date, timeZoneRef.current);
    if (navigateToDayAtRef.current) {
      navigateToDayAtRef.current(instant);
    } else {
      onViewRef.current('day');
      setActiveInstantRef.current(instant);
    }
  }, []);

  const drillDownToDayViewRef = useRef(drillDownToDayView);
  drillDownToDayViewRef.current = drillDownToDayView;

  const notifyRangeChange = useCallback((range: { start: { toString(): string }; end: { toString(): string } }) => {
    const polyfillRange = scheduleXRangeToPolyfill(range);
    setToolbarLabel(formatScheduleXRangeLabel(polyfillRange, viewRef.current));
    onRangeChangeRef.current?.(instantIntervalFromScheduleXRange(range));
  }, []);

  const notifyRangeChangeRef = useRef(notifyRangeChange);
  notifyRangeChangeRef.current = notifyRangeChange;

  const scheduleXEvents = useMemo(
    () => events.map((event) => concreteEventToScheduleX(event, timeZone)),
    [events, timeZone],
  );
  const eventCountByDate = useMemo(() => coachCalendarEventCountByDate(events, timeZone), [events, timeZone]);
  const scheduleXEventsRef = useRef(scheduleXEvents);
  scheduleXEventsRef.current = scheduleXEvents;
  const eventCountByDateRef = useRef(eventCountByDate);
  eventCountByDateRef.current = eventCountByDate;
  const calendarRef = useRef<ReturnType<typeof useCalendarApp>>(null);
  const calendarRootRef = useRef<HTMLDivElement>(null);

  const syncMonthGridDayCells = useCallback(() => {
    if (viewRef.current !== 'month' || !calendarRootRef.current) {
      return;
    }
    syncScheduleXMonthGridDayCells(calendarRootRef.current, eventCountByDateRef.current);
  }, []);

  const scheduleMonthGridSync = useCallback(() => {
    flushScheduleXMonthGridSync(syncMonthGridDayCells);
  }, [syncMonthGridDayCells]);

  const selectedDate = useMemo(
    () => toScheduleXApiPlainDate(activeInstant.toZonedDateTimeISO(timeZone).toPlainDate()),
    [activeInstant, timeZone],
  );

  const sxViews = useMemo(() => buildScheduleXViews(views), [views]);

  const todayGridDate = useMemo(() => Temporal.Now.zonedDateTimeISO(timeZone).toPlainDate().toString(), [timeZone]);

  const calendar = useCalendarApp(
    {
      views: sxViews,
      defaultView: toScheduleXViewName(view),
      selectedDate,
      timezone: timeZone,
      // Keep week/month when the viewport narrows; toolbar + URL own view state. Mobile layout uses CSS horizontal scroll.
      isResponsive: false,
      weekOptions: {
        gridStep: SCHEDULE_X_GRID_STEP_MINUTES,
        gridHeight: SCHEDULE_X_WEEK_GRID_HEIGHT_PX,
      },
      monthGridOptions: {
        nEventsPerDay: SCHEDULE_X_MONTH_GRID_N_EVENTS_PER_DAY,
      },
      callbacks: {
        onRender: () => {
          calendarRenderedRef.current = true;

          if (viewRef.current === 'month') {
            calendarControlsRef.current.setMonthGridOptions({
              nEventsPerDay: SCHEDULE_X_MONTH_GRID_VISIBLE_EVENTS,
            });
          }

          calendarRef.current?.events.set(scheduleXEventsRef.current);

          if (appliedTimezoneRef.current !== timeZoneRef.current) {
            try {
              calendarControlsRef.current.setTimezone(timeZoneRef.current);
              appliedTimezoneRef.current = timeZoneRef.current;
            } catch {
              // current-time plugin reads $app.config; skip if not fully initialized yet
            }
          }

          if (viewRef.current === 'month') {
            syncMonthGridDayCells();
            return;
          }
          try {
            scrollController.scrollTo(scrollTimeRef.current);
          } catch {
            // scroll-controller is not ready until the time grid exists
          }
        },
        onRangeUpdate: (range) => {
          notifyRangeChangeRef.current(range);
        },
        onEventClick: (calendarEvent, uiEvent) => {
          const anchor = (uiEvent.target as HTMLElement | null)?.closest('[data-session-schedule-event-id]');
          setPopoverAnchorEl(anchor instanceof HTMLElement ? anchor : null);
          setActiveEventId(String(calendarEvent.id));
          const eventEl = (uiEvent.target as HTMLElement | null)?.closest('.sx__time-grid-event, .sx__date-grid-event');
          if (eventEl instanceof HTMLElement) {
            eventEl.blur();
          }
        },
        onSelectedDateUpdate: (date) => {
          syncUrlFromPlainDate(fromScheduleXPlainDate(date));
        },
        onClickDate: (date) => {
          if (viewRef.current !== 'month') {
            return;
          }
          drillDownToDayViewRef.current(fromScheduleXPlainDate(date));
        },
        onClickPlusEvents: (date) => {
          drillDownToDayViewRef.current(fromScheduleXPlainDate(date));
        },
      },
    },
    [calendarControls, scrollController, currentTime],
  );
  calendarRef.current = calendar;

  useEffect(() => {
    if (!calendar) {
      return;
    }
    calendarControls.setMonthGridOptions({ nEventsPerDay: SCHEDULE_X_MONTH_GRID_VISIBLE_EVENTS });
  }, [calendar, calendarControls]);

  useEffect(() => {
    if (calendar) {
      calendar.events.set(scheduleXEvents);
    }
    if (view === 'month') {
      scheduleMonthGridSync();
    }
  }, [calendar, scheduleXEvents, view, scheduleMonthGridSync]);

  useEffect(() => {
    const root = calendarRootRef.current;
    if (!root || view !== 'month') {
      return;
    }

    let rafId: number | undefined;
    const runSync = () => {
      syncMonthGridDayCells();
    };

    const resizeObserver = new ResizeObserver(() => {
      scheduleMonthGridSync();
    });
    resizeObserver.observe(root);

    const mutationObserver = new MutationObserver(() => {
      if (rafId !== undefined) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        rafId = undefined;
        runSync();
      });
    });
    mutationObserver.observe(root, { childList: true, subtree: true });

    scheduleMonthGridSync();

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      if (rafId !== undefined) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [view, scheduleMonthGridSync, syncMonthGridDayCells]);

  useEffect(() => {
    if (!calendar) {
      return;
    }
    // Month "+N events" / day click: calendar is already on day before URL view=day lands.
    if (pendingDayDrillDownRef.current) {
      if (view !== 'day') {
        return;
      }
      pendingDayDrillDownRef.current = false;
    }
    calendarControls.setView(toScheduleXViewName(view));
    calendarControls.setDate(toScheduleXApiPlainDate(activeInstant.toZonedDateTimeISO(timeZone).toPlainDate()));
    if (view === 'month') {
      calendarControls.setMonthGridOptions({ nEventsPerDay: SCHEDULE_X_MONTH_GRID_VISIBLE_EVENTS });
    }

    const range = calendarControls.getRange();
    if (range) {
      notifyRangeChangeRef.current(range);
    }

    scrollTimeGridToRef();
  }, [calendar, calendarControls, view, activeInstant, timeZone, scrollTimeGridToRef]);

  useEffect(() => {
    if (!calendar || !calendarRenderedRef.current || appliedTimezoneRef.current === timeZone) {
      return;
    }
    try {
      calendarControls.setTimezone(timeZone);
      appliedTimezoneRef.current = timeZone;
    } catch {
      // defer until the next onRender when Schedule-X plugins are fully wired
    }
  }, [calendar, calendarControls, timeZone]);

  const navigate = useCallback(
    (direction: 'previous' | 'next' | 'today') => {
      if (!calendar) {
        return;
      }
      const controls = calendarControls;

      if (direction === 'today') {
        const todayPlain = Temporal.Now.zonedDateTimeISO(timeZone).toPlainDate();
        controls.setDate(toScheduleXApiPlainDate(todayPlain));
        const now = Temporal.Now.instant();
        setActiveInstantRef.current(now);
        scrollTimeRef.current = scheduleXTimeString(now, timeZone);
        scrollTimeGridToRef();
        return;
      }

      const currentDate = fromScheduleXPlainDate(controls.getDate());
      const amount = direction === 'previous' ? -1 : 1;
      const sxView = controls.getView();

      let nextDate: Temporal.PlainDate;
      if (sxView === 'month-grid') {
        nextDate = currentDate.add({ months: amount });
      } else if (sxView === 'week') {
        nextDate = currentDate.add({ days: 7 * amount });
      } else {
        nextDate = currentDate.add({ days: amount });
      }

      const scheduleXDate = toScheduleXApiPlainDate(nextDate);
      controls.setDate(scheduleXDate);
      syncUrlFromPlainDate(nextDate);
    },
    [calendar, calendarControls, timeZone, syncUrlFromPlainDate, scrollTimeGridToRef],
  );

  const handleViewChange = useCallback(
    (newView: CalendarView) => {
      onView(newView);
      calendarControls.setView(toScheduleXViewName(newView));
    },
    [calendarControls, onView],
  );

  const activeEvent = useMemo(
    () => (activeEventId ? eventsById.get(activeEventId) : undefined),
    [activeEventId, eventsById],
  );

  return (
    <EventContext.Provider value={eventContext}>
      <ScheduleXEventsByIdContext.Provider value={eventsById}>
        <div
          ref={calendarRootRef}
          className="session-schedule-calendar-schedule-x h-full"
          data-calendar-view={view}
          data-today-grid-date={todayGridDate}
        >
          {/* Today column only — not selectedDate (Schedule-X .is-selected). Grid lines omitted on today. */}
          <style>{`.session-schedule-calendar-schedule-x[data-today-grid-date="${todayGridDate}"] .sx__time-grid-day[data-time-grid-date="${todayGridDate}"] { background-color: #eaf6ff; background-image: none; }`}</style>
          <ScheduleXToolbar
            label={displayToolbarLabel}
            view={view}
            views={views}
            onPrevious={() => navigate('previous')}
            onNext={() => navigate('next')}
            onToday={() => navigate('today')}
            onViewChange={handleViewChange}
          />
          <ScheduleXCalendar calendarApp={calendar} customComponents={SCHEDULE_X_CUSTOM_COMPONENTS} />
          {activeEvent && (
            <SessionScheduleEventPopover
              event={activeEvent}
              anchorEl={popoverAnchorEl}
              open
              onClose={clearActiveEvent}
            />
          )}
          <SessionNextClassTooltip events={events} timeZone={timeZone} variant="schedule-x" />
        </div>
      </ScheduleXEventsByIdContext.Provider>
    </EventContext.Provider>
  );
}

//#endregion
